import type { ProcessorOptions, Track } from "livekit-client";
import { ref } from "vue";
import { vbgEngine } from "./vbg/engine";
import type { BackgroundSource } from "./gifBackground";

export const loadProcessorError = ref(false);

// Тонкий адаптер над vbgEngine: наружу — прежний публичный API LiveKit-процессора, внутрь —
// один общий воркер на страницу. Жизненным циклом кадров и вкладки владеет движок.
export class BlurProcessor {
  name = "blur-video-processor";
  processedTrack?: MediaStreamTrack;
  blur: number;
  mode: "blur" | "background";
  backgroundSource: BackgroundSource | null;
  destroyed = false;

  private sourceTrack?: MediaStreamTrack;
  private endedListener?: () => void;

  constructor(
    blur: number,
    mode: "blur" | "background" = "blur",
    backgroundSource?: BackgroundSource | null,
  ) {
    this.blur = blur;
    this.mode = mode;
    this.backgroundSource = backgroundSource || null;
  }

  async init(opts: ProcessorOptions<Track.Kind>) {
    this.destroyed = false;
    const track = opts.track;
    this.sourceTrack = track;
    this.endedListener = () => {
      void this.destroy();
    };
    track.addEventListener("ended", this.endedListener);
    try {
      this.processedTrack = await vbgEngine.start({
        track,
        mode: this.mode,
        blur: this.blur,
        backgroundUrl: this.backgroundUrl(),
      });
    } catch (err) {
      // Ошибку не глотаем: по ней videoBlur просто не ставит процессор. loadProcessorError
      // остаётся за segmenter.ts — здесь лишь одноразовая загрузка модели, а не отказ кадра.
      if (track.readyState === "ended" && this.endedListener) {
        // Трек завершился до подписки: событие `ended` уже не придёт, слушатель осиротел бы.
        track.removeEventListener("ended", this.endedListener);
        this.endedListener = undefined;
      }
      throw err;
    }
  }

  async restart(opts: ProcessorOptions<Track.Kind>) {
    await this.destroy();
    await this.init(opts);
  }

  async destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.sourceTrack && this.endedListener) {
      this.sourceTrack.removeEventListener("ended", this.endedListener);
    }
    this.sourceTrack = undefined;
    this.endedListener = undefined;
    await vbgEngine.stop();
    this.processedTrack = undefined;
  }

  setNewBlurSize(newSize: number) {
    this.blur = newSize;
    vbgEngine.update({ blur: newSize });
  }

  setMode(mode: "blur" | "background") {
    this.mode = mode;
    vbgEngine.update({ bg: mode === "background" ? "image" : "blur", backgroundUrl: this.backgroundUrl() });
  }

  setBackgroundSource(source: BackgroundSource | null) {
    this.backgroundSource = source;
    vbgEngine.update({ bg: "image", backgroundUrl: source?.url ?? null });
  }

  private backgroundUrl(): string | null {
    return this.mode === "background" ? this.backgroundSource?.url ?? null : null;
  }
}
